<script setup lang="ts">
import {computed, ref, watch, inject, nextTick} from "vue";
import {useFormDataStore} from "@/stores/formDataStore";
import { useI18n } from 'vue-i18n';
import DataRow from '@/components/molecule/DataRow.vue';
import type { FormValidation } from '@/types/form-validation';
import type { FormEnergyCommunity } from '@/stores/communityApiStore';
import { MemberType } from '../../types/src/enums';

const formDataStore = useFormDataStore();
const formValidation = inject('$formValidation') as FormValidation;
const { t } = useI18n();

const props = defineProps<{
  communityPublic?: FormEnergyCommunity;
  isActive?: boolean;
}>()

import { isNoMemberJoin } from '@/lib/utils/communityFlags';
const noMemberJoin = computed(()=> isNoMemberJoin(props.communityPublic?.communityType));
const isCompany = computed(() => formDataStore.formData.isCompany === MemberType.COMPANY);
import { METERING_POINT_DIRECTION } from "@/lib/constants/meteringPoint";
import { isMultiDso, shouldSkipWireInfo } from '@/lib/utils/communityFlags';

const atLeastOneProducer = computed(() => {
  return formDataStore.formData.meteringPointsAssociated.some(mp => mp.meteringPointDirection === METERING_POINT_DIRECTION.GENERATION);
});
const multiDsoMode = computed(() => isMultiDso(props.communityPublic?.communityType));
const isEpu = computed(() => formDataStore.formData.legal === "Einpersonenunternehmen");
const epuHasRegistrationNumber = computed(() => formDataStore.formData.billingData.identificationType === "FBN");
const skipWireInfo = computed(()=> shouldSkipWireInfo(props.communityPublic?.ecn));

// Get consents from the store with dynamic descriptions based on community data
const customAgreements = computed(() => {
  const cp = props.communityPublic;
  if (!cp) return formDataStore.getCustomAgreements('', '', '', '', []);
  return formDataStore.getCustomAgreements(
    cp.name,
    cp.addressPostal?.name ?? '',
    cp.legal,
    cp.communityType,
    (cp as unknown as { customAgreements?: any[] }).customAgreements ?? [],
    (cp as unknown as { needsSepaUpfront?: boolean }).needsSepaUpfront ?? false,
    (cp as unknown as { sepaAgreement?: any }).sepaAgreement ?? null
  );
});

watch(() => props.isActive,
  (nowActive: boolean | undefined) => {
    if (nowActive) {
      // Reset standard agreements
      formDataStore.formData.agreedJoin = false;
      formDataStore.formData.agreedDataProcessing = false;
      formDataStore.formData.agreedNewsletterConsent = false;
      // Reset any existing custom agreements
      formDataStore.resetCustomAgreements();
    }
  }
);

function resolveIdentificationType(type: string){
  if(type === "ZVR")
    return "ZVR-Zahl";
  if(type === "FBN")
    return "Firmenbuchnummer";
  if(type === "DateOfBirth")
    return "Geburtsdatum";

  return "sonstiges";
}

function getTaxSelectionLabel(selection: string) {
  switch(selection) {
    case 'USt.-pflichtig':
      return 'USt.-pflichtig (20%)';
    case 'Pauschalierung (Land-/Forstwirt)':
      return 'Pauschalierung (Land-/Forstwirt) (13%)';
    case 'Kleinunternehmer (unecht Steuerbefreit)':
      return 'Kleinunternehmer (unecht Steuerbefreit) (0%)';
    case 'Nichtunternehmer lt. Photovoltaikerlass':
      return 'Nichtunternehmer lt. Photovoltaikerlass (0%)';
    case 'Manuell eingeben':
      return `Manuell (${formDataStore.formData.billingData.taxInfo.taxRatePercentPoints}%)`;
    default:
      return selection;
  }
}

const formValid = ref(false);
const theForm = ref<any>(null);

async function formValidates() {
  if (!theForm.value) return true;
  const result = await theForm.value.validate();
  const valid = result.valid;

  if (!valid) {
    formValidation.scrollToFirstValidationError({
      nextTick
    });
  }

  return valid;
}

defineExpose({formValidates});
</script>

<template>
<div>
  <div class="background-divider"></div>
  <div class="pa-4">
    <v-form ref="theForm" v-model="formValid">
      <v-row no-gutters>
        <v-col cols="12">
          <p class="textFormHeadline">{{ t('memberJoinStepper.summaryAndConsent') }}</p>

          <!-- Summary section with DataRow components -->
          <div class="mb-6">
            <!-- Company information -->
            <v-row v-if="isCompany" class="mb-6 ml-4 mr-4">
              <v-col cols="12" md="6">
                <p class="textFormHeadline">{{ t('summaryLabels.company') }}</p>
                <DataRow :label="t('summaryLabels.company')">
                  {{ formDataStore.formData.contactData.companyName }}
                </DataRow>
                <DataRow :label="t('summaryLabels.legalForm')">
                  {{ formDataStore.formData.legal }}
                </DataRow>
                <div v-if="isEpu && !epuHasRegistrationNumber">
                  <DataRow v-if="isEpu && !epuHasRegistrationNumber" :label="t('summaryLabels.birthDate')">
                    {{ formDataStore.formData.contactData.dateOfBirth }}
                  </DataRow>
                </div>
                <div v-else>
                  <DataRow v-if="(!isEpu || isEpu && epuHasRegistrationNumber) && formDataStore.formData.billingData.identification" :label="resolveIdentificationType(formDataStore.formData.billingData.identificationType)">
                    {{ formDataStore.formData.billingData.identification }}
                  </DataRow>
                </div>
                <DataRow v-if="formDataStore.formData.confirmedSmeState" :label="t('summaryLabels.smeStatus')">
                  {{ formDataStore.formData.confirmedSmeState ? t('yes') : t('no') }}
                </DataRow>
              </v-col>
            </v-row>

            <!-- Personal data, billing address, and bank info in one row -->
            <v-row no-gutters class="mb-6 ml-4 mr-4">
              <!-- Personal data -->
              <v-col cols="12" md="4">
                <p class="textFormHeadline" v-if="isCompany">{{ t('contactPerson') }}</p>
                <p class="textFormHeadline" v-else>{{ t('personalData') }}</p>
                <DataRow :label="t('summaryLabels.name')">
                  {{ formDataStore.formData.contactData.nameFirst }} {{ formDataStore.formData.contactData.nameLast }}
                </DataRow>
                <DataRow :label="t('summaryLabels.phone')">
                  {{ formDataStore.formData.contactData.phoneNumber }}
                </DataRow>
                <DataRow :label="t('summaryLabels.email')">
                  {{ formDataStore.formData.contactData.emailAddress }}
                </DataRow>
                <div v-if="!isCompany">
                  <DataRow v-if="!isCompany" :label="t('summaryLabels.birthDate')">
                    {{ formDataStore.formData.contactData.dateOfBirth }}
                  </DataRow>
                  <DataRow v-if="!isCompany && formDataStore.formData.billingData.identification" :label="resolveIdentificationType(formDataStore.formData.billingData.identificationType)">
                    {{ formDataStore.formData.billingData.identification }}
                  </DataRow>
                </div>
              </v-col>

              <!-- Billing address -->
              <v-col cols="12" md="4">
                <p class="textFormHeadline">{{ t('billingAddress') }}</p>
                <DataRow :label="t('summaryLabels.address')">
                  {{ formDataStore.formData.billingData.addressPostal.address }}
                </DataRow>
                <DataRow v-if="formDataStore.formData.billingData.addressPostal.addressExtra.length" :label="t('summaryLabels.extra')">
                  {{ formDataStore.formData.billingData.addressPostal.addressExtra }}
                </DataRow>
                <DataRow :label="t('summaryLabels.zipCity')">
                  {{ formDataStore.formData.billingData.addressPostal.zip }} {{ formDataStore.formData.billingData.addressPostal.city }}
                </DataRow>
              </v-col>

              <!-- Bank Account Info Section -->
              <v-col v-if="!skipWireInfo" cols="12" md="4">
                <p class="textFormHeadline">{{ t('bankDetails') }}</p>
                <DataRow :label="t('billingLabels.accountHolder')">
                  {{ formDataStore.formData.billingData.wireInfo.accountHolder }}
                </DataRow>
                <DataRow :label="t('billingLabels.iban')">
                  {{ formDataStore.formData.billingData.wireInfo.iban }}
                </DataRow>
                <DataRow :label="t('billingLabels.bic')">
                  {{ formDataStore.formData.billingData.wireInfo.bic }}
                </DataRow>
              </v-col>
            </v-row>

            <!-- Tax Info Section -->
            <v-row no-gutters v-if="atLeastOneProducer" class="mb-6 ml-4 mr-4">
              <v-col cols="12" md="6">
                <p class="textFormHeadline">{{ t('vatDetails') }}</p>
                <DataRow :label="t('summaryLabels.vatType')">
                  {{ getTaxSelectionLabel(formDataStore.formData.billingData.taxInfo.taxSelection) }}
                </DataRow>
                <DataRow v-if="formDataStore.formData.billingData.taxInfo.taxNote" :label="t('billingLabels.note')">
                  {{ formDataStore.formData.billingData.taxInfo.taxNote }}
                </DataRow>
                <DataRow v-if="formDataStore.formData.billingData.taxInfo.uid" :label="t('billingLabels.uidNumber')">
                  {{ formDataStore.formData.billingData.taxInfo.uid }}
                </DataRow>
              </v-col>
            </v-row>

            <!-- Metering points section - full width -->
            <v-row no-gutters class="mb-6 ml-4 mr-4">
              <v-col cols="12">
                <p  v-if="formDataStore.formData.meteringPointsAssociated.length === 0" class="textFormHeadline">{{ t('meteringPoints') }}</p>

                <div v-if="formDataStore.formData.meteringPointsAssociated.length === 0">
                  <p class="empty-metering-point">{{ t('noMeteringPoints') }}</p>
                </div>

                <div v-else>
                  <div v-for="(meteringPoint, index) in formDataStore.formData.meteringPointsAssociated" :key="index">
                    <div v-if="index > 0" class="summary-divider mt-4 mb-4"></div>
                    <p  class="textFormHeadline">{{ t('meteringPoint') }} {{ (index + 1)}}</p>

                    <v-row>
                      <v-col cols="12" md="6">
                          <DataRow :label="t('summaryLabels.designation')">
                          {{ meteringPoint.nameFriendly }}
                        </DataRow>
                        <DataRow :label="t('summaryLabels.meteringPoint')">
                          {{ meteringPoint.meteringPoint }}
                        </DataRow>
                        <DataRow :label="t('summaryLabels.address')">
                          {{ meteringPoint.addressPostal.address }}
                        </DataRow>
                        <DataRow v-if="meteringPoint.addressPostal.addressExtra.length" :label="t('summaryLabels.extra')">
                          {{ meteringPoint.addressPostal.addressExtra }}
                        </DataRow>
                        <DataRow :label="t('summaryLabels.zipCity')">
                          {{ meteringPoint.addressPostal.zip }} {{ meteringPoint.addressPostal.city }}
                        </DataRow>
                        <DataRow v-if="meteringPoint.ecnDso && multiDsoMode" :label="t('meteringPointLabels.gridOperator')">
                          {{ meteringPoint.ecnDso }}
                        </DataRow>
                      </v-col>

                      <v-col cols="12" md="6">
                        <DataRow :label="t('summaryLabels.direction')">
                          {{ meteringPoint.meteringPointDirection === METERING_POINT_DIRECTION.CONSUMPTION ? t('consumption') : t('generation') }}
                        </DataRow>
                        <DataRow v-if="meteringPoint.participationFactor" :label="t('participationFactor')">
                          {{ meteringPoint.participationFactor }}%
                        </DataRow>

                        <div v-if="meteringPoint.meteringPointDirection === METERING_POINT_DIRECTION.CONSUMPTION">
                          <DataRow v-if="meteringPoint.assumptionEnergyPerYearKwh.length" :label="t('summaryLabels.demand')">
                            {{ meteringPoint.assumptionEnergyPerYearKwh }} kWh p.a.
                          </DataRow>
                        </div>
                        <div v-else>
                          <DataRow v-if="meteringPoint.assumptionEnergyPerYearKwh.length" :label="t('summaryLabels.surplus')">
                            {{ meteringPoint.assumptionEnergyPerYearKwh }} kWh p.a.
                          </DataRow>
                          <DataRow :label="t('summaryLabels.system')">
                            {{ meteringPoint.plant.generationConfiguration }}, {{ meteringPoint.plant.generationType }}
                          </DataRow>
                          <DataRow v-if="meteringPoint.plant.lineCapacity.length" :label="t('meteringPointLabels.bottleneckCapacity')">
                            {{ meteringPoint.plant.lineCapacity }} kWp
                          </DataRow>
                          <DataRow v-if="meteringPoint.energyStorageKwh.length" :label="t('summaryLabels.storage')">
                            {{ meteringPoint.energyStorageKwh }} kWh
                          </DataRow>
                        </div>
                      </v-col>
                    </v-row>
                  </div>
                </div>
              </v-col>
            </v-row>

            <!-- Notes section -->
            <v-row no-gutters class="mb-6 ml-4 mr-4">
              <v-col cols="12">
                <p class="textFormHeadline">{{ t('summaryLabels.notes') }}</p>
                <DataRow :label="t('summaryLabels.notes')">
                  <div v-if="formDataStore.formData.notes && formDataStore.formData.notes.length > 0">
                    {{ formDataStore.formData.notes }}
                  </div>
                  <div v-else>
                    {{ t('noNotes') }}
                  </div>
                </DataRow>
              </v-col>
            </v-row>
          </div>

          <v-divider></v-divider>
          <p class="textDescriptor mt-4">{{ t(noMemberJoin?'memberJoinStepper.checkDataGea':'memberJoinStepper.checkData') }}</p>
          <p class="textDescriptor">{{ t(noMemberJoin?'memberJoinStepper.boardApprovalGea':'memberJoinStepper.boardApproval') }}</p>
          <p class="textDescriptor">{{ t('memberJoinStepper.privacyStatement') }}</p>

          <v-row no-gutters>
            <v-col cols="12">
              <!-- Dynamic Consent Checkboxes -->
              <div v-for="(agreement, index) in customAgreements" :key="agreement.id" class="mb-2">
                <v-checkbox
                  density="compact"
                  class="checkboxes"
                  :class="{ 'firstCheckbox': index === 0 }"
                  :rules="agreement.required ? [formValidation.formValidationRequiredCheckbox] : []"
                  v-model="formDataStore.formData[agreement.id]"
                >
                  <template #label>
                    <span>{{ agreement.description }}</span>
                    <a v-if="agreement.link" :href="agreement.link.url" target="_blank" class="ml-2">
                      {{ agreement.link.text }}
                    </a>
                  </template>
                </v-checkbox>
              </div>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </v-form>
  </div>
  <div class="background-divider"></div>
</div>
</template>

<style scoped lang="scss">
.summary-divider {
  max-width: 380px;
  margin: 10px 0;
  border-top: 1px solid #ddd;
}

.checkboxes {
  margin-top: 8px;
}

.firstCheckbox {
  margin-top: 20px;
}

.background-divider {
  background-color: rgba(var(--v-theme-dark-grey-3), 0.12);
  height: 8px;
  width: 100%;
}

.textFormHeadline {
  font-size: var(--font-size-h6);
  font-weight: 500;
  margin-bottom: 12px;
  margin-top: 8px;
}

.textDescriptor {
  margin-bottom: 4px;
}

.empty-metering-point {
  margin-top: 8px;
  font-size: var(--font-size-body-1);
  color: rgb(var(--v-theme-info));
}

/* Add styles for v-col to prevent long text from overflowing */
:deep(.v-col) {
  overflow-wrap: break-word;
  word-wrap: break-word;
}
</style>
